/* eslint-disable functional/no-loop-statements */
import { getEnvVars } from '@order/utils';

import { onetrustStyles } from './styles';

// ─────────────────────────────────────────────────────────────────────────────

export function loadOneTrust() {
  // NOTE: If the `HideCookiesBanner` cookie is present, we should not load the
  //       OneTrust to prevent the banner from showing up.
  const shouldPreventLoading = document.cookie.includes('HideCookiesBanner');

  if (shouldPreventLoading) return;

  const styles = createOtStyles();
  const scripts = getOneTrustScripts();

  if (styles) {
    document.head.append(styles);
  }

  scripts.forEach((script) => {
    document.body.append(script);
  });

  observeCookieBanner(customizeCookiesBannerAfterLoad);
}

// ─────────────────────────────────────────────────────────────────────────────

function getOneTrustScripts() {
  const otAutoBlockScript = createOtAutoBlockScript();
  const otSDKStubScript = createOtSDKStubScript();
  const otOptAnonWrapper = createOtOptAnonWrapper();

  return [otAutoBlockScript, otSDKStubScript, otOptAnonWrapper].filter(
    Boolean,
  ) as HTMLScriptElement[];
}

function createOtAutoBlockScript() {
  if (!ONE_TRUST_SCRIPT_DOMAIN_KEY) return;

  return createScriptIfDoesntExist({
    id: 'ot-auto-block-script',
    src: `https://cdn.cookielaw.org/consent/${ONE_TRUST_SCRIPT_DOMAIN_KEY}/OtAutoBlock.js`,
  });
}

function createOtSDKStubScript() {
  if (!ONE_TRUST_SCRIPT_DOMAIN_KEY) return;

  const script = createScriptIfDoesntExist({
    id: 'ot-sdk-stub-script',
    src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
  });

  if (!script) return;

  script.dataset.domainScript = ONE_TRUST_SCRIPT_DOMAIN_KEY;

  return script;
}

function createOtOptAnonWrapper() {
  const script = createScriptIfDoesntExist({
    id: 'ot-opt-anon-wrapper-script',
  });
  const code = 'function OptanonWrapper() { }';

  if (!script) return;

  script.append(document.createTextNode(code));

  return script;
}

function createScriptIfDoesntExist(params: CreateScriptIfDoesntExistParams) {
  const { id, src } = params;

  const isScriptAlreadyAdded = checkIfScriptExists(id);

  if (isScriptAlreadyAdded) return;

  const script = document.createElement('script');

  script.id = id;

  if (src) {
    script.src = src;
  }

  return script;
}

function createOtStyles() {
  const id = 'ot-styles';
  const isAlreadyAdded = checkIfStyleExists(id);

  if (isAlreadyAdded) return;

  const style = document.createElement('style');

  style.id = id;
  style.textContent = onetrustStyles;

  return style;
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Checks if a script with the specified ID exists in the document.
 * Helpful for avoiding duplicated actions, which can cause HMR to fail.
 */
function checkIfScriptExists(id: string) {
  const maybeScript = document.querySelector(`script[id="${id}"]`);

  return maybeScript !== null;
}

/**
 * Checks if a style element with the specified ID exists in the document.
 * Helpful for avoiding duplicated actions, which can cause HMR to fail.
 */
function checkIfStyleExists(id: string) {
  const maybeStyle = document.querySelector(`style[id="${id}"]`);

  return maybeStyle !== null;
}

function customizeCookiesBannerAfterLoad() {
  addCustomCloseButton();
}

function addCustomCloseButton() {
  const cookiesBannerContainer = getCookiesBannerContainer();

  if (!cookiesBannerContainer) return;

  const buttonsGroup = cookiesBannerContainer.querySelector(
    '#onetrust-button-group',
  );

  if (!buttonsGroup) return;

  const hasCustomCloseButton = Boolean(buttonsGroup.querySelector(`#${CUSTOM_CLOSE_BUTTON_ID}`)); // prettier-ignore
  const firstButton = buttonsGroup?.children?.[0];

  if (hasCustomCloseButton) return;

  const closeButton = document.createElement('button');

  closeButton.id = CUSTOM_CLOSE_BUTTON_ID;
  closeButton.type = 'button';
  closeButton.innerHTML = 'Got it';
  closeButton.ariaLabel = 'Close cookies banner';
  closeButton.addEventListener('click', closeCookiesBanner);

  if (firstButton) {
    firstButton?.before?.(closeButton);
    closeButton.focus();

    return;
  }

  buttonsGroup?.append?.(closeButton);
  closeButton?.focus?.();
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Observes the body for changes and detects when #onetrust-consent-sdk is appended
 */
function observeCookieBanner(callback: () => void) {
  const isAlreadyAdded = Boolean(getCookiesBannerContainer());

  if (isAlreadyAdded) {
    callback();

    return;
  }

  const observer = new MutationObserver((mutations) => {
    const onBannerAdded = createOnBannerAddedFactory(callback, observer);

    for (const mutation of mutations) {
      const addedNode = mutation?.addedNodes?.[0];
      const isBannerDetected = onBannerAdded(addedNode);

      if (isBannerDetected) {
        break;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: false });

  return () => observer.disconnect();
}

function createOnBannerAddedFactory(
  callback: () => void,
  observer: MutationObserver,
) {
  return (node: Node): boolean => {
    const castedNode = node as Element | null;

    if (!castedNode?.matches(COOKIES_BANNER_CONTAINER_SELECTOR)) {
      return false;
    }

    observer.disconnect();
    callback();

    return true;
  };
}

function getCookiesBannerContainer() {
  return document.querySelector(COOKIES_BANNER_CONTAINER_SELECTOR);
}

function closeCookiesBanner() {
  const globalOneTrust = globalThis as GlobalOneTrust;

  if (!globalOneTrust?.OneTrust) return;

  globalOneTrust.OneTrust?.Close?.();
}

// ─── Constants ───────────────────────────────────────────────────────────────

const COOKIES_BANNER_CONTAINER_SELECTOR = '#onetrust-consent-sdk';
const CUSTOM_CLOSE_BUTTON_ID = 'onetrust-close-btn-custom';

const { ONE_TRUST_SCRIPT_DOMAIN_KEY } = getEnvVars();

// ─── Types ───────────────────────────────────────────────────────────────────

type CreateScriptIfDoesntExistParams = { id: string; src?: string };

type GlobalOneTrust = {
  OneTrust?: {
    Close: () => void;
  };
};
